import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import MagneticButton from "@/components/MagneticButton";

function Particles({ count = 22 }) {
    const parts = Array.from({ length: count }).map((_, i) => ({
        id: i,
        left: Math.random() * 100,
        top: Math.random() * 100,
        size: 2 + Math.random() * 4,
        delay: Math.random() * 12,
        dur: 14 + Math.random() * 18,
        drift: (Math.random() - 0.5) * 80,
        opacity: 0.12 + Math.random() * 0.35,
    }));
    return (
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
            {parts.map((p) => (
                <span
                    key={p.id}
                    className="absolute rounded-full bg-brand-orange"
                    style={{
                        left: `${p.left}%`,
                        top: `${p.top}%`,
                        width: p.size,
                        height: p.size,
                        opacity: p.opacity,
                        animation: `cursus-detail-float ${p.dur}s ease-in-out ${p.delay}s infinite alternate`,
                        ["--drift"]: `${p.drift}px`,
                        filter: "blur(0.5px)",
                    }}
                />
            ))}
            <style>{`
                @keyframes cursus-detail-float {
                    0%   { transform: translate(0, 0); }
                    50%  { transform: translate(var(--drift), -40px); }
                    100% { transform: translate(0, 0); }
                }
            `}</style>
        </div>
    );
}

export default function CursusDetailFinalCTA({ cursus }) {
    return (
        <section
            data-testid="cursus-detail-final-cta"
            className="relative flex min-h-screen items-center justify-center overflow-hidden bg-brand-ink text-white"
        >
            <Particles />
            <div className="absolute inset-0 grain pointer-events-none opacity-35" />
            <div className="pointer-events-none absolute inset-x-0 top-1/2 h-[1px] -translate-y-1/2 bg-gradient-to-r from-transparent via-brand-orange/25 to-transparent" />

            <div className="relative mx-auto max-w-6xl px-6 py-28 text-center">
                <motion.div
                    initial={{ opacity: 0, letterSpacing: "0.3em" }}
                    whileInView={{ opacity: 1, letterSpacing: "0.35em" }}
                    viewport={{ once: true }}
                    transition={{ duration: 1 }}
                    className="text-[11px] font-semibold uppercase tracking-[0.35em] text-brand-orange"
                >
                    Déjà plus de 320 kinés ont suivi nos cursus
                </motion.div>

                <motion.h2
                    initial={{ opacity: 0, y: 60, filter: "blur(10px)" }}
                    whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                    viewport={{ once: true, margin: "-20%" }}
                    transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                    className="mt-10 font-display italic text-brand-orange"
                    style={{
                        fontSize: "clamp(5.5rem, 18vw, 14rem)",
                        lineHeight: 0.88,
                        letterSpacing: "-0.04em",
                    }}
                >
                    Composez.
                </motion.h2>

                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.9, delay: 0.3 }}
                    className="mx-auto mt-10 max-w-2xl text-lg leading-relaxed text-white/65 md:text-xl"
                >
                    Votre parcours <span className="text-white">{cursus.name.replace("Cursus ", "")}</span>.
                    Trois formations. <em className="italic text-brand-orange">Un engagement.</em>
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.9, delay: 0.5 }}
                    className="mt-14 flex flex-col items-center justify-center gap-4 sm:flex-row"
                >
                    <MagneticButton strength={0.25}>
                        <a
                            href="#sessions"
                            data-testid="cursus-detail-cta-sessions"
                            className="group inline-flex items-center gap-3 rounded-full bg-brand-orange px-12 py-6 text-base font-semibold text-white transition-all hover:bg-[#d9772c]"
                            style={{ boxShadow: "0 20px 60px -10px rgba(232,135,60,0.55)" }}
                        >
                            Voir les prochaines sessions
                            <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                        </a>
                    </MagneticButton>
                    <MagneticButton strength={0.18}>
                        <Link
                            to="/contact"
                            data-testid="cursus-detail-cta-contact"
                            className="group inline-flex items-center gap-2 rounded-full border border-white/25 px-8 py-5 text-sm font-semibold text-white/90 transition-all hover:border-white/60 hover:text-white"
                        >
                            Des questions ?
                        </Link>
                    </MagneticButton>
                </motion.div>
            </div>
        </section>
    );
}
